<template>
  <div class="w-full">
    <div class="relative w-full">
      <!-- Budget Label -->
      <div class="absolute -top-3 right-3 z-[70]">
        <span class="bg-[#e0f7ef] text-[#4ECF84] text-xs font-medium px-3 py-1 rounded-[20px]">Add Max Budget</span>
      </div>
      <!-- Currency Symbol -->
      <span class="absolute left-4 top-1/2 transform -translate-y-1/2 text-lg md:text-base">
        {{ currencySymbol }}
      </span>
      
      <input
        v-model="displayValue"
        type="text"
        placeholder="Any"
        class="w-full h-11 pl-8 rounded-full shadow-sm bg-white outline-none transition-all text-lg text-left md:text-base border border-[#2F3033] placeholder:text-lg md:placeholder:text-base"
        :class="[
          displayValue ? 'pr-10' : 'pr-4',
          isMobile ? 'focus:border-[#4ECF84] focus:ring-2 focus:ring-[#4ECF84]/15' : 'focus:border-[#2F3033]'
        ]"
        @input="handleInput"
        @blur="handleBlur"
        @keypress="validateNumericInput"
      />
      
      <!-- Clear Button -->
      <button
        v-if="displayValue"
        @click="clearValue"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
        type="button"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Props
const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: ''
  }
})

// Emits
const emit = defineEmits(['update:modelValue'])

// Reactive data
const rawValue = ref(props.modelValue || '')
const displayValue = ref('')
const currentCurrency = ref('AUD')
const isMobile = ref(false)

const checkScreenSize = () => {
  isMobile.value = typeof window !== 'undefined' ? window.innerWidth < 768 : false
}

// Currency symbol mapping
const currencySymbols = {
  'AUD': '$',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'CAD': '$',
  'JPY': '¥',
  'CNY': '¥'
}

// Computed currency symbol
const currencySymbol = computed(() => {
  return currencySymbols[currentCurrency.value] || '$'
})

// Load stored currency from localStorage
const loadStoredCurrency = () => {
  try {
    const stored = localStorage.getItem('currency')
    if (stored) {
      currentCurrency.value = stored
    }
  } catch (error) {
    console.log('Could not load currency from localStorage:', error)
  }
}

// Handle currency change events
const handleCurrencyChange = (event) => {
  const newCurrency = event?.detail
  if (newCurrency && currencySymbols[newCurrency]) {
    currentCurrency.value = newCurrency
  }
}

// Format number with commas
const formatNumber = (num) => {
  if (!num || num === '') return ''
  const number = typeof num === 'string' ? num.replace(/,/g, '') : num.toString()
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// Remove commas and get raw number
const getRawNumber = (formattedValue) => {
  if (!formattedValue) return ''
  return formattedValue.replace(/,/g, '')
}

// Handle input changes
const handleInput = (event) => {
  const inputValue = event.target.value
  const rawNumber = getRawNumber(inputValue)
  
  // Only allow numbers
  if (rawNumber && !/^\d+$/.test(rawNumber)) {
    // Restore previous valid value
    event.target.value = displayValue.value
    return
  }
  
  // Update raw value and emit
  rawValue.value = rawNumber
  emit('update:modelValue', rawNumber ? Number(rawNumber) : '')
  
  // Format and update display
  displayValue.value = formatNumber(rawNumber)
}

// Handle blur event to ensure proper formatting
const handleBlur = (event) => {
  const inputValue = event.target.value
  const rawNumber = getRawNumber(inputValue)
  
  if (rawNumber) {
    displayValue.value = formatNumber(rawNumber)
  } else {
    displayValue.value = ''
  }
}

// Validate numeric input (prevent non-numeric characters)
const validateNumericInput = (event) => {
  const char = String.fromCharCode(event.which)
  // Allow numbers, backspace, delete, and comma
  if (!/[0-9,]/.test(char) && event.which !== 8 && event.which !== 46) {
    event.preventDefault()
  }
}

// Clear the input value
const clearValue = () => {
  rawValue.value = ''
  displayValue.value = ''
  emit('update:modelValue', '')
}

// Lifecycle hooks
onMounted(() => {
  // Load initial currency
  loadStoredCurrency()
  
  // Set mobile state
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
  
  // Listen for currency changes from other components
  window.addEventListener('currency-changed', handleCurrencyChange)
  
  // Set initial value if provided
  if (props.modelValue) {
    rawValue.value = props.modelValue.toString()
    displayValue.value = formatNumber(props.modelValue.toString())
  }
})

onUnmounted(() => {
  // Clean up event listener
  window.removeEventListener('currency-changed', handleCurrencyChange)
  window.removeEventListener('resize', checkScreenSize)
})

// Watch for prop changes
import { watch } from 'vue'
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    rawValue.value = newValue.toString()
    displayValue.value = formatNumber(newValue.toString())
  } else {
    rawValue.value = ''
    displayValue.value = ''
  }
})
</script>

<style scoped>
/* Remove number input arrows */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}


</style>
